import { CommonModule } from '@angular/common';

import { ChangeDetectionStrategy,Component,Input,OnDestroy } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { MatBadgeModule } from '@angular/material/badge';
import { MatLegacyButtonModule } from '@angular/material/legacy-button';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { MatLegacyFormFieldModule } from '@angular/material/legacy-form-field';
import { MatLegacyInputModule } from '@angular/material/legacy-input';
import { MatLegacyTooltipModule as MatTooltipModule } from '@angular/material/legacy-tooltip';
import { ResolveData } from '@angular/router';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { Store } from '@ngrx/store';
import { BehaviorSubject,combineLatest,firstValueFrom,Observable, Subject,switchMap } from 'rxjs';
import { filter,map, takeUntil,withLatestFrom } from 'rxjs/operators';
import { DetailPersonPopupComponent,DetailPersonPopupComponentDialogData } from 'src/app/dave-person-module/components/detail-person-popup/detail-person-popup.component';
import { PersonEntity } from '../../../../dave-data-module/entities/person.entity';
import { Person2EntityEntityTypeEnum } from '../../../../dave-data-module/entities/person2entity.entity';
import { ComplaintTypeResolver } from '../../../../dave-data-module/guards/complaintType.resolver';
import { PersonResolver } from '../../../../dave-data-module/guards/person.resolver';
import { Person2EntityResolver } from '../../../../dave-data-module/guards/person2Entity.resolver';
import { State } from '../../../../dave-data-module/State';
import { Person2EntityActionTypes } from '../../../../dave-data-module/State/actions/person2entity.action';
import { complaintTypesFeatureKey } from '../../../../dave-data-module/State/reducers/complaintType.reducer';
import { personsFeatureKey } from '../../../../dave-data-module/State/reducers/person.reducer';
import { person2EntityFeatureKey } from '../../../../dave-data-module/State/reducers/person2Entity.reducer';
import { getComplaintTypes } from '../../../../dave-data-module/State/selectors/complaintType.selectors';
import { getPersons } from '../../../../dave-data-module/State/selectors/person.selectors';
import { getPerson2Entities } from '../../../../dave-data-module/State/selectors/person2entity.selectors';
import { getPersonTypes } from '../../../../dave-data-module/State/selectors/personType.selectors';
import { DaveDoubleIconModule } from '../../../../dave-double-icon/dave-double-icon.module';
import { DaveListCardModule } from '../../../../dave-list-card/dave-list-card.module';
import { SelectPersonsComponent,SelectPersonsComponentDialogData } from '../../../../dave-person-module/components/select-persons/select-persons.component';
import { AppButtonModule } from '../../../../dave-utils-module/app-button-module/app-button.module';
import { AppFilterModule } from '../../../../dave-utils-module/app-filter-module/app-filter.module';
import {
FILTER_TYPE_SEARCH_MULTI_SELECT,
IFilterTypeSearchMultiSelectValue
} from '../../../../dave-utils-module/app-filter-module/app-filter/app-filter.component';
import { isNotNullOrUndefined } from '../../../../helper/helper';
import { ContactBookMeta } from '../../../../helper/page-metadata';
import { PersonListEntryComponent } from '../../../../person-list-entry/person-list-entry.component';
import { FilterTypes } from '../../../../services/default-filter.service';

interface IInvolvedPersonFilterValues {
    [FilterTypes.Roles]: IFilterTypeSearchMultiSelectValue<number>[];
}

@Component({
    selector: 'app-involved-persons-list[entityId][entityType][complainTypeId]',
    standalone: true,
    imports: [
        CommonModule,
        DaveListCardModule,
        AppButtonModule,
        MatTooltipModule,
        DaveDoubleIconModule,
        PersonListEntryComponent,
        FontAwesomeModule,
        AppFilterModule,
        MatBadgeModule,
        MatLegacyButtonModule,
        MatLegacyFormFieldModule,
        MatLegacyInputModule,
        ReactiveFormsModule,
    ],
    templateUrl: './involved-persons-list.component.html',
    styleUrls: ['./involved-persons-list.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InvolvedPersonsListComponent implements OnDestroy {
    public static readonly RequiredResolvers: ResolveData = {
        [personsFeatureKey]: PersonResolver,
        [complaintTypesFeatureKey]: ComplaintTypeResolver,
        [person2EntityFeatureKey]: Person2EntityResolver,
    };
    @Input() expanded = false;
    entityId$ = new BehaviorSubject<number>(null);
    @Input() set entityId(v: number) {
        if (v !== this.entityId$.value) {
            this.entityId$.next(v);
        }
    }
    entityType$ = new BehaviorSubject<Person2EntityEntityTypeEnum>(null);
    @Input() set entityType(v: Person2EntityEntityTypeEnum) {
        if (v !== this.entityType$.value) {
            this.entityType$.next(v);
        }
    }
    complainTypeId$ = new BehaviorSubject<number>(null);
    @Input() set complainTypeId(v: number) {
        if (v !== this.complainTypeId$.value) {
            this.complainTypeId$.next(v);
        }
    }
    @Input() canAdd = false;
    @Input() addButtonText = '';
    @Input() addHeaderText: string | null = null;

    @Input() headline = 'Beteiligte Personen';
    @Input() headerIcon: IconProp = ContactBookMeta.Icon;

    @Input() newPersonDefaultValues: Partial<PersonEntity> | null = null;

    protected RoleFilterValues$: Observable<IFilterTypeSearchMultiSelectValue[]> = this.store.select(getPersonTypes).pipe(
        filter(isNotNullOrUndefined),
        map((personTypes) =>
            personTypes.map((type) => ({
                id: type.Id,
                label: type.Name,
            })),
        ),
    );

    private RoleLabelMap$ = this.store.select(getPersonTypes).pipe(
        filter(isNotNullOrUndefined),
        map((personTypes) => new Map<number, string>(personTypes.map((t) => [t.Id as number, t.Name as string]))),
    );
    private roleLabelMapSync = new Map<number, string>();

    FilterValues$ = new BehaviorSubject<{ [FilterTypes.Roles]: IFilterTypeSearchMultiSelectValue<number>[] }>({ [FilterTypes.Roles]: [] });
    FilterSettings$ = this.RoleFilterValues$.pipe(
        map((roles) => {
            return [
                {
                    Name: FilterTypes.Roles,
                    Type: FILTER_TYPE_SEARCH_MULTI_SELECT,
                    Label: 'Rolle',
                    Icon: null,
                    Values: roles.map<IFilterTypeSearchMultiSelectValue>((t) => ({ id: t.id, label: t.label })),
                },
            ];
        }),
    );
    FilterAmount$ = this.FilterValues$.pipe(map((filterValues) => (filterValues[FilterTypes.Roles]?.length ? 1 : null)));

    private destroy$ = new Subject<void>();

    constructor(private store: Store<State>, private dialog: MatDialog) {
        // Snapshot der Rollen, damit diese in der Compare Funktion zur Suche verwendet werden können
        this.RoleLabelMap$.pipe(takeUntil(this.destroy$)).subscribe((roleLabelMap) => this.roleLabelMapSync = roleLabelMap);
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    public ShowBeteiligteList$: Observable<PersonEntity[]> = combineLatest([
        this.store.select(getPersons),
        // this.store.select(getFilterdPersonTypesForBeteiligtePersonen),
        this.entityId$,
        this.entityType$,
        this.complainTypeId$,
        this.store.select(getPerson2Entities),
    ]).pipe(
        map(([persons, /*personTypes,*/ entityId, entityType, complainTypeId, p2e]) => {
            // hier muss auf getFilterdPersonTypesForBeteiligtePersonen geprüft werden, es kann nicht auf den complaint type geprüft werden da der nicht immer gesetzt ist
            // const complainTypeId = complaintTypes.find((ct) => ct.Name.toLowerCase().includes('beteiligte person'))?.Id;

            const pIds = p2e?.filter((p) =>
                p.EntityType === entityType &&
                p.EntityId === entityId &&
                (complainTypeId == null || p.ComplaintTypeId === complainTypeId)
            ).map((p) => p.PersonId);
            return persons?.filter((p) => pIds.includes(p.Id) && !p.Deleted);
        }),
        switchMap((persons) => {
            return this.FilterValues$.pipe(
                map((filterValues) => {
                    return this.applyInvolvedPersonFilter(persons, filterValues);
                }),
            )
        })
    );

    private applyInvolvedPersonFilter(persons: PersonEntity[], filterValues: IInvolvedPersonFilterValues): PersonEntity[] {
        const roles = filterValues[FilterTypes.Roles]?.map((v) => v.id);
        return persons?.filter((p) => {
            if (roles?.length) {
                return roles.includes(p.PersonTypeId);
            }
            return true;
        });
    }

    public customPersonSearchCompare = (s: string, p: PersonEntity): boolean => {
        const searchQuery = (s ?? '').trim().toLowerCase();
        if (!searchQuery) return true;

        const roleLabel = this.roleLabelMapSync?.get(p.PersonTypeId) ?? '';
        const searchStrings = [
            p.Firstname,
            p.Lastname,
            p.SecondFirstname,
            p.SecondLastname,
            p.CompanyName,
            roleLabel,
            p.Email,
            p.SecondMail,
            p.Organisation,
            p.PhoneNumber,
            p.MobileNumber,
            p.WorkNumber,
            p.FaxNumber,
        ].filter(Boolean)
            .map((v) => String(v).toLowerCase());

        return searchStrings.some((v) => v.includes(searchQuery));
    };

    protected readonly ContactBookMeta = ContactBookMeta;
    GetRouteForPerson = ({ Id }: { Id: number }) => ['/', ContactBookMeta.Path, Id];

    public async OpenPersonDialog() {
        const allP2es = await firstValueFrom(this.store.select(getPerson2Entities));
        const entityType = this.entityType$.value;
        const entityId = this.entityId$.value;
        const complaintTypeId = this.complainTypeId$.value;

        const p2es = allP2es.filter((pe) => pe.EntityType === entityType && pe.EntityId === entityId);

        let selectedIds = p2es.filter((p) => complaintTypeId == null || p.ComplaintTypeId === complaintTypeId).map((p2e) => p2e.PersonId);
        this.dialog
            .open<SelectPersonsComponent, SelectPersonsComponentDialogData, PersonEntity[]>(SelectPersonsComponent, {
                ...SelectPersonsComponent.DefaultConfig,
                data: {
                    EntityId: entityId,
                    HeaderText: this.addHeaderText ?? undefined,
                    ButtonText: this.addButtonText,
                    SelectedPersonIds: selectedIds,
                    newPersonDefaultValues: this.newPersonDefaultValues ?? undefined,
                },
            })
            .afterClosed()
            .pipe(
                filter(isNotNullOrUndefined),
                withLatestFrom(
                    this.store.select(getPerson2Entities).pipe(
                        filter(isNotNullOrUndefined),
                        map((pes) => pes.filter((pe) => pe.EntityType === entityType && pe.EntityId === entityId)),
                    ),
                ),
                withLatestFrom(this.store.select(getComplaintTypes)),
            )
            .subscribe(([[selectedPersons, personEntities], complaintTypes]) => {
                let addIds: number[] = [];
                let delIds: number[] = [];

                selectedPersons.forEach((p) => {
                    if (!selectedIds.includes(p.Id)) {
                        addIds.push(p.Id);
                    }
                });
                personEntities.forEach((pe) => {
                    if (selectedPersons.filter((p) => p.Id === pe.PersonId && (complaintTypeId == null || pe.ComplaintTypeId === complaintTypeId)).length === 0) {
                        delIds.push(pe.Id);
                    }
                });

                addIds.forEach((pId) => {
                    this.store.dispatch(
                        Person2EntityActionTypes.AddPerson2Entity({
                            Payload: {
                                personId: pId,
                                entityId: entityId,
                                entityType: entityType,
                                complaintTypeId: complaintTypeId,
                            },
                        }),
                    );
                });

                delIds.forEach((id) => {
                    this.store.dispatch(
                        Person2EntityActionTypes.DeletePerson2Entity({
                            Payload: {
                                id: id,
                            },
                        }),
                    );
                });
            });
    }

    public openBeteiligterDialog(entry: PersonEntity) {
        this.dialog.open<DetailPersonPopupComponent, DetailPersonPopupComponentDialogData>(DetailPersonPopupComponent, {
            ...DetailPersonPopupComponent.DefaultConfig,
            data: {
                personId: entry.Id,
                headline: this.headline,
            },
        });
    }
}
